import type { LsproPlayerConfig } from '../../use-player-config';
import type { PlayerApi, PlayerConfig } from '@movingimage-evp/player-library';
import type { HTMLAttributes } from 'react';

import { Player, PlayerEvent } from '@movingimage-evp/player-library';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchVideoDetails } from '../../../../../common/fetch-video-details';
import { useSelectedLanguage } from '../../../../providers/selected-language-provider';
import { usePlayerConfig } from '../../use-player-config';
import { UnmutePopup } from '../unmute-popup';

import styles from './cover-video-player.module.css';

type CoverVideoPlayerProps = {
  webcastId: string;
  channelId: string;
  videoId: string;
  accountId?: string;
  lsproId?: string;
  title?: string;
  primaryColor?: string;
  iconColor?: string;
  logoUrl?: string;
  looping?: boolean;
  autoplay?: boolean;
  stepButtonsAreVisible?: boolean;
  state?: string;
  offsetInSeconds?: number;
  onPlayerTimeUpdate?: (currentTime: number) => void;
  onOverlayVisibilityChange?: (visible: boolean) => void;
} & HTMLAttributes<HTMLDivElement>;

export function CoverVideoPlayer({
  webcastId,
  channelId,
  videoId,
  accountId,
  lsproId,
  title,
  primaryColor,
  iconColor,
  logoUrl,
  looping,
  autoplay,
  stepButtonsAreVisible,
  state,
  offsetInSeconds,
  onPlayerTimeUpdate,
  onOverlayVisibilityChange,
  ...props
}: CoverVideoPlayerProps) {
  const { t } = useTranslation();
  const { selectedLanguage } = useSelectedLanguage();
  const playerApiRef = useRef<PlayerApi | null>(null);
  const [playerData, setPlayerData] = useState<PlayerConfig>();

  const lsproPlayerConfig: LsproPlayerConfig | undefined = useMemo(() => {
    if (!playerData) return undefined;

    const youboraAnalytics = playerData?.plugins?.youboraAnalytics;

    return {
      webcastId,
      contentId: videoId,
      playbackUrl: 'undefined',
      title,
      accountId,
      lsproId,
      state,
      language: selectedLanguage?.code,
      channelId: youboraAnalytics?.['content.customDimension.3'],
      channelName: youboraAnalytics?.['content.channel'],
      groupId: youboraAnalytics?.['content.customDimension.6'],
      videoManagerId: youboraAnalytics?.['content.customDimension.1'],
    };
  }, [accountId, lsproId, playerData, state, title, videoId, webcastId, selectedLanguage]);

  const { plugins } = usePlayerConfig(lsproPlayerConfig) || {};

  const [unmutePopupVisible, setUnmutePopupVisible] = useState(false);
  const [hasCoverVideoError, setHasCoverVideoError] = useState(false);

  const playerConfig = useMemo(() => {
    if (!playerData) return undefined;

    return {
      ...playerData,
      plugins,
      settings: {
        primaryColor,
        iconColor,
        logoUrl,
        autoplay,
        looping,
        stepButtonsAreVisible,
        videoLayout: 'fill',
      },
      metadata: { ...playerData.metadata, title: title || '' },
    } as PlayerConfig;
  }, [playerData, plugins, primaryColor, iconColor, logoUrl, looping, autoplay, stepButtonsAreVisible, title]);

  useEffect(() => {
    // Reset the state so that the Player is properly re-rendered when new IDs come in
    setPlayerData(undefined);
    setUnmutePopupVisible(false);
    setHasCoverVideoError(false);

    let ignoreResult = false;
    fetchVideoDetails(videoId, channelId)
      .then(({ playerData }) => {
        if (ignoreResult) return;
        setPlayerData(playerData);
      })
      .catch(() => setHasCoverVideoError(true));

    return () => {
      ignoreResult = true;
    };
  }, [videoId, channelId]);

  const handleVolumeChange = useCallback(() => {
    if (!playerApiRef.current?.getMuted()) {
      setUnmutePopupVisible(false);
    }
  }, []);

  const handleOnTimeUpdate = useCallback(() => {
    if (onPlayerTimeUpdate && playerApiRef.current) {
      onPlayerTimeUpdate(playerApiRef.current.getCurrentTime());
    }
  }, [onPlayerTimeUpdate]);

  const playVideo = useCallback(
    async (api: PlayerApi) => {
      try {
        if (api.getPaused()) await api.play();
      } catch {
        try {
          api.setMuted(true);
          setUnmutePopupVisible(true);
          api.addEventListener(PlayerEvent.VOLUME_CHANGE, handleVolumeChange);

          await api.play();
        } catch {
          setHasCoverVideoError(true);
        }
      }
    },
    [handleVolumeChange]
  );

  useEffect(() => {
    if (!unmutePopupVisible) {
      playerApiRef.current?.removeEventListener(PlayerEvent.VOLUME_CHANGE, handleVolumeChange);
    }
  }, [handleOnTimeUpdate, handleVolumeChange, unmutePopupVisible]);

  useEffect(() => {
    return playerApiRef.current?.removeEventListener(PlayerEvent.TIME_UPDATE, handleOnTimeUpdate);
  }, [handleOnTimeUpdate]);

  const playerReadyHandler = useCallback(
    (api: PlayerApi) => {
      playerApiRef.current = api;

      if (offsetInSeconds) {
        playerApiRef.current.setCurrentTime(offsetInSeconds);
      }

      if (autoplay) playVideo(api);

      if (onPlayerTimeUpdate) {
        api.addEventListener(PlayerEvent.TIME_UPDATE, handleOnTimeUpdate);
      }
    },
    [autoplay, handleOnTimeUpdate, offsetInSeconds, onPlayerTimeUpdate, playVideo]
  );

  const unmuteVideo = () => {
    setUnmutePopupVisible(false);
    playerApiRef.current?.setMuted(false);
  };

  return (
    <div data-testid="viewer-cover-video" {...props}>
      {playerConfig && (
        <Player {...playerConfig} onReady={playerReadyHandler} onOverlayVisibilityChange={onOverlayVisibilityChange} />
      )}

      {unmutePopupVisible && !hasCoverVideoError && (
        <UnmutePopup
          title={t('viewer.views.unmutePopup.title.videoStarted')}
          onClose={() => setUnmutePopupVisible(false)}
          onUnMute={unmuteVideo}
        />
      )}

      {hasCoverVideoError && (
        <div className={styles.coverVideoError}>
          {t('errors.coverVideo.noAccess')}
          <small>{t('errors.contactAdministrator')}</small>
        </div>
      )}
    </div>
  );
}
